
import Link from '@/components/compat/Link';
import { useMemo, useState } from 'react';
import { ArchiveRestore, History, Pencil, Plus, RotateCcw, ThumbsDown, ThumbsUp, Trash2 } from 'lucide-react';
import { PainelConfirmDialog } from '@/components/painel/PainelFeedbackDialog';
import { PainelContentHistoryModal } from '@/components/painel/PainelContentHistoryModal';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { painelTableShellClass } from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import {
  canDeleteGuide,
  canRestoreGuide,
  canSendGuideToReview,
  PUBLICATION_STATUS_BADGE_CLASS,
  PUBLICATION_STATUS_LABELS,
} from '@/lib/cms-publication';
import { useCms } from './cms/cms-context';
import { useGuideFeedback } from '@/components/help-center/guide-feedback-context';
import { buildGuideFeedbackKeyFromArticle } from '@/lib/guide-feedback';
import { PainelGuideFeedbackSection } from '@/components/painel/PainelGuideFeedbackSection';
import { usePainelText } from '@/components/painel/painel-texts-context';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { usePainelSaveFeedback } from '@/components/painel/PainelSaveFeedback';
import { logPainelContentActivity, logPainelSubmitForReview } from '@/lib/painel-activity-log';
import type { CmsArticle } from '@/lib/cms-types';
import {
  PainelCategoryFilterBar,
  PainelGuideCategoryBadge,
  resolveGuideCategoryTone,
} from '@/components/painel/painel-category-visuals';

const wrap = painelTableShellClass;

const guideRowActionBtnClass =
  'rounded-xl text-blue-100 hover:bg-white/10 hover:text-white';

const guideRowTooltipClass =
  'border-white/10 bg-[#0a1468] font-poppins text-xs text-blue-50 shadow-lg shadow-black/30';

const guideActionTooltipProps = {
  side: 'top' as const,
  align: 'center' as const,
  collisionPadding: 12,
};

const panelTabsListClass =
  'h-auto gap-1 rounded-xl border border-white/10 bg-white/[0.04] p-1';

const panelTabsTriggerClass =
  'rounded-lg px-4 py-2 font-poppins text-sm text-slate-300 data-[state=active]:bg-white/10 data-[state=active]:text-white data-[state=active]:shadow-none';

function formatUpdatedAt(iso: string) {
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function PainelArticlesPage() {
  const pageText = usePainelText('guias');
  const { session, role } = usePainelSession();
  const { showSaveSuccess } = usePainelSaveFeedback();
  const {
    articles,
    trashedArticles,
    categories,
    hydrated,
    deleteArticle,
    restoreArticle,
    permanentlyDeleteArticle,
    updateArticle,
    getCategoryById,
  } = useCms();
  const { hydrated: feedbackHydrated, getStats, negativeFeedbackForGuide } = useGuideFeedback();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [forceDeleteId, setForceDeleteId] = useState<string | null>(null);
  const [historyTarget, setHistoryTarget] = useState<{ id: string; label: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'library' | 'trash'>('library');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const canReturnToReview = canSendGuideToReview(role);
  const isSuperAdmin = role === 'super_admin';
  const canDelete = canDeleteGuide(session);
  const canRestore = canRestoreGuide(session);

  const ordered = [...articles].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );

  const trashedOrdered = [...trashedArticles].sort((a, b) => {
    const aTime = a.deletedAt ? new Date(a.deletedAt).getTime() : 0;
    const bTime = b.deletedAt ? new Date(b.deletedAt).getTime() : 0;
    return bTime - aTime;
  });

  const tabSourceList = activeTab === 'trash' ? trashedOrdered : ordered;

  const categoryFilterOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const article of tabSourceList) {
      counts.set(article.categoryId, (counts.get(article.categoryId) ?? 0) + 1);
    }
    return categories
      .filter((c) => counts.has(c.id))
      .map((c) => ({
        id: c.id,
        label: c.name,
        count: counts.get(c.id) ?? 0,
        tone: resolveGuideCategoryTone(c),
      }));
  }, [categories, tabSourceList]);

  const applyCategoryFilter = (list: CmsArticle[]) => {
    if (!categoryFilter) return list;
    return list.filter((a) => a.categoryId === categoryFilter);
  };

  const filteredOrdered = applyCategoryFilter(ordered);
  const filteredTrashed = applyCategoryFilter(trashedOrdered);

  const handleRestore = (article: CmsArticle) => {
    restoreArticle(article.id);
    logPainelContentActivity(session, {
      action: 'update',
      resource: 'guide',
      resourceId: article.id,
      resourceLabel: `Restaurou: ${article.title}`,
    });
    showSaveSuccess({
      title: 'Guia restaurado',
      description: `“${article.title}” voltou à biblioteca.`,
    });
  };

  return (
    <TooltipProvider delayDuration={250}>
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
            {pageText.title}
          </h1>
          <p className="mt-1 font-inter text-sm text-slate-400">{pageText.description}</p>
        </div>
        <Button
          asChild
          className={cn(
            'rounded-xl border border-sky-400/40 bg-sky-500/90 font-poppins text-white shadow-lg shadow-sky-950/30 hover:bg-sky-400'
          )}
        >
          <Link href="/painel/guias/new">
            <Plus className="mr-2 h-4 w-4" />
            Novo guia
          </Link>
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as 'library' | 'trash')}
        className="space-y-6"
      >
        <TabsList className={panelTabsListClass}>
          <TabsTrigger value="library" className={panelTabsTriggerClass}>
            Biblioteca
            {hydrated ? (
              <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-[11px] tabular-nums">
                {ordered.length}
              </span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="trash" className={panelTabsTriggerClass}>
            Lixeira
            {hydrated ? (
              <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-[11px] tabular-nums">
                {trashedOrdered.length}
              </span>
            ) : null}
          </TabsTrigger>
        </TabsList>

        {hydrated && categoryFilterOptions.length > 0 ? (
          <PainelCategoryFilterBar
            options={categoryFilterOptions}
            value={categoryFilter}
            onChange={setCategoryFilter}
            allCount={tabSourceList.length}
          />
        ) : null}

        <TabsContent value="library" className="mt-0 space-y-6">
          <div className={wrap}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] border-collapse text-left font-inter text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.04]">
                    <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                      Título
                    </th>
                    <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                      Categoria
                    </th>
                    <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                      Estado
                    </th>
                    <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                      <span className="inline-flex items-center gap-1">
                        <ThumbsUp className="h-3.5 w-3.5" />
                        Útil
                      </span>
                    </th>
                    <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                      <span className="inline-flex items-center gap-1">
                        <ThumbsDown className="h-3.5 w-3.5" />
                        Não ajudou
                      </span>
                    </th>
                    <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                      Última atualização
                    </th>
                    <th className="px-4 py-3 text-right font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {!hydrated || !feedbackHydrated ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                        Carregando…
                      </td>
                    </tr>
                  ) : filteredOrdered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                        {categoryFilter
                          ? 'Nenhum guia nesta categoria.'
                          : 'Nenhum guia ainda. Crie categorias e depois adicione o primeiro guia.'}
                      </td>
                    </tr>
                  ) : (
                    filteredOrdered.map((a) => {
                      const cat = getCategoryById(a.categoryId);
                      const guideKey = buildGuideFeedbackKeyFromArticle(a);
                      const stats = getStats(guideKey);
                      const messageCount = negativeFeedbackForGuide(guideKey).length;
                      return (
                        <tr key={a.id} className="transition-colors hover:bg-white/[0.03]">
                          <td className="max-w-[220px] truncate px-4 py-3 font-poppins font-medium text-blue-50 md:max-w-xs">
                            {a.title}
                          </td>
                          <td className="px-4 py-3">
                            <PainelGuideCategoryBadge category={cat} />
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={cn(
                                'inline-flex rounded-full px-2.5 py-0.5 font-poppins text-[11px] font-semibold uppercase tracking-wide ring-1',
                                PUBLICATION_STATUS_BADGE_CLASS[a.status]
                              )}
                            >
                              {PUBLICATION_STATUS_LABELS[a.status]}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 tabular-nums text-emerald-200/90">
                            {stats.likes}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 tabular-nums text-rose-200/90">
                            {stats.dislikes}
                            {messageCount > 0 ? (
                              <span className="ml-1.5 inline-flex items-center rounded-full border border-violet-400/20 bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-violet-200/90">
                                {messageCount} msg
                              </span>
                            ) : null}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-400">
                            {formatUpdatedAt(a.updatedAt)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-2">
                              {canReturnToReview && a.status === 'published' ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className={guideRowActionBtnClass}
                                      aria-label="Enviar para revisão"
                                      onClick={() => {
                                        updateArticle(a.id, { status: 'pending_review' });
                                        logPainelSubmitForReview(session, {
                                          resource: 'guide',
                                          resourceId: a.id,
                                          resourceLabel: a.title,
                                        });
                                        showSaveSuccess({
                                          title: 'Enviado para revisão',
                                          description: `“${a.title}” voltou à fila de revisão.`,
                                        });
                                      }}
                                    >
                                      <RotateCcw className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent {...guideActionTooltipProps} className={guideRowTooltipClass}>
                                    Enviar para revisão
                                  </TooltipContent>
                                </Tooltip>
                              ) : null}
                              {isSuperAdmin ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className={guideRowActionBtnClass}
                                      aria-label="Histórico de alterações"
                                      onClick={() =>
                                        setHistoryTarget({ id: a.id, label: a.title })
                                      }
                                    >
                                      <History className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent {...guideActionTooltipProps} className={guideRowTooltipClass}>
                                    Histórico de alterações
                                  </TooltipContent>
                                </Tooltip>
                              ) : null}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className={guideRowActionBtnClass}
                                    asChild
                                    aria-label="Editar guia"
                                  >
                                    <Link href={`/painel/guias/${a.id}/edit`}>
                                      <Pencil className="h-4 w-4" />
                                    </Link>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent {...guideActionTooltipProps} className={guideRowTooltipClass}>
                                  Editar guia
                                </TooltipContent>
                              </Tooltip>
                              {canDelete ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className={guideRowActionBtnClass}
                                      onClick={() => setDeleteId(a.id)}
                                      aria-label="Mover para a lixeira"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent {...guideActionTooltipProps} className={guideRowTooltipClass}>
                                    Mover para a lixeira
                                  </TooltipContent>
                                </Tooltip>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <PainelGuideFeedbackSection />
        </TabsContent>

        <TabsContent value="trash" className="mt-0 space-y-6">
          <div className={wrap}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse text-left font-inter text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.04]">
                    <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                      Título
                    </th>
                    <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                      Categoria
                    </th>
                    <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                      Estado anterior
                    </th>
                    <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                      Excluído em
                    </th>
                    <th className="px-4 py-3 text-right font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {!hydrated ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                        Carregando…
                      </td>
                    </tr>
                  ) : filteredTrashed.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-slate-400">
                        {categoryFilter
                          ? 'Nenhum guia excluído nesta categoria.'
                          : 'A lixeira está vazia. Guias excluídos aparecem aqui e podem ser restaurados.'}
                      </td>
                    </tr>
                  ) : (
                    filteredTrashed.map((a) => {
                      const cat = getCategoryById(a.categoryId);
                      return (
                        <tr key={a.id} className="transition-colors hover:bg-white/[0.03]">
                          <td className="max-w-[220px] truncate px-4 py-3 font-poppins font-medium text-blue-50 md:max-w-xs">
                            {a.title}
                          </td>
                          <td className="px-4 py-3">
                            <PainelGuideCategoryBadge category={cat} />
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={cn(
                                'inline-flex rounded-full px-2.5 py-0.5 font-poppins text-[11px] font-semibold uppercase tracking-wide ring-1',
                                PUBLICATION_STATUS_BADGE_CLASS[a.status]
                              )}
                            >
                              {PUBLICATION_STATUS_LABELS[a.status]}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-400">
                            {a.deletedAt ? formatUpdatedAt(a.deletedAt) : '—'}
                          </td>
                          <td className="px-4 py-3 pr-6">
                            <div className="flex justify-end gap-2">
                              {canRestore ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className={guideRowActionBtnClass}
                                      aria-label="Restaurar guia"
                                      onClick={() => handleRestore(a)}
                                    >
                                      <ArchiveRestore className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent {...guideActionTooltipProps} className={guideRowTooltipClass}>
                                    Restaurar guia
                                  </TooltipContent>
                                </Tooltip>
                              ) : null}
                              {isSuperAdmin ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className={guideRowActionBtnClass}
                                      aria-label="Excluir permanentemente"
                                      onClick={() => setForceDeleteId(a.id)}
                                    >
                                      <Trash2 className="h-4 w-4 text-rose-300" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent {...guideActionTooltipProps} className={guideRowTooltipClass}>
                                    Excluir permanentemente
                                  </TooltipContent>
                                </Tooltip>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p className="font-inter text-xs text-slate-400/70">
            Guias na lixeira deixam de aparecer no site. Restaure para voltar ao estado anterior ou
            exclua permanentemente (apenas Super Admin).
          </p>
        </TabsContent>
      </Tabs>

      <p className="font-inter text-xs text-slate-400/70">
        Categorias disponíveis: {categories.length}. Os guias publicados aparecem no site após
        guardar com estado &quot;Publicado&quot;.
      </p>

      <PainelContentHistoryModal
        open={historyTarget !== null}
        onOpenChange={(open) => !open && setHistoryTarget(null)}
        resource="guide"
        resourceId={historyTarget?.id ?? null}
        resourceLabel={historyTarget?.label ?? ''}
      />

      <PainelConfirmDialog
        open={deleteId !== null}
        onOpenChange={(o) => !o && setDeleteId(null)}
        title="Mover para a lixeira?"
        description="O guia deixará de aparecer no site e na biblioteca. Pode restaurá-lo depois na aba Lixeira."
        confirmLabel="Mover para a lixeira"
        onConfirm={() => {
          if (deleteId) {
            const article = articles.find((a) => a.id === deleteId);
            deleteArticle(deleteId);
            logPainelContentActivity(session, {
              action: 'delete',
              resource: 'guide',
              resourceId: deleteId,
              resourceLabel: article?.title ?? 'Guia',
            });
            showSaveSuccess({
              title: 'Guia movido para a lixeira',
              description: article ? `“${article.title}” pode ser restaurado na aba Lixeira.` : undefined,
            });
          }
          setDeleteId(null);
        }}
      />

      <PainelConfirmDialog
        open={forceDeleteId !== null}
        onOpenChange={(o) => !o && setForceDeleteId(null)}
        title="Excluir permanentemente?"
        description="Esta ação é irreversível. O guia será removido definitivamente da base de dados."
        confirmLabel="Excluir permanentemente"
        onConfirm={() => {
          if (forceDeleteId) {
            const article = trashedArticles.find((a) => a.id === forceDeleteId);
            permanentlyDeleteArticle(forceDeleteId);
            logPainelContentActivity(session, {
              action: 'delete',
              resource: 'guide',
              resourceId: forceDeleteId,
              resourceLabel: article ? `Excluiu permanentemente: ${article.title}` : 'Guia',
            });
            showSaveSuccess({
              title: 'Guia excluído permanentemente',
              description: article ? `“${article.title}” foi removido de forma definitiva.` : undefined,
            });
          }
          setForceDeleteId(null);
        }}
      />
    </div>
    </TooltipProvider>
  );
}
